import type { Storefront } from '@/hooks/useStorefront';
import { rewriteForIT } from '@/lib/rewriteForIT';
import { rewriteForAL, shouldScrubForAL } from '@/lib/rewriteForAL';

/**
 * Per-storefront SEO override layer (P-SEO §1b).
 *
 * CH is EXCLUDED at every layer:
 *   - DB: `seo_overrides.storefront` CHECK rejects 'ch' (§1a migration).
 *   - Helper: `resolveSeoText` early-returns `base` when storefront === 'ch'
 *     BEFORE touching the override map. Byte-identical to today for CH.
 *   - Hook (`useSeoText`): does not invoke useQuery for CH — zero read,
 *     zero subscription.
 */

export type SeoScope = 'route' | 'product' | 'category';

/**
 * Which text field on a page an override targets.
 * Extend cautiously — every new field needs UI wiring + admin support.
 */
export type SeoField =
  | 'title'
  | 'description'
  | 'h1'
  | 'hero_subtitle'
  | 'hero_text';

export interface SeoOverrideLookup {
  scope: SeoScope;
  scope_key: string;
  field: SeoField;
  locale: string;
}

export type SeoOverrideMap = Map<string, string>;

/** Stable string key used inside the override Map. */
export function seoOverrideKey(k: SeoOverrideLookup): string {
  return `${k.scope}::${k.scope_key}::${k.field}::${k.locale}`;
}

/**
 * Pure resolver. No React, no Supabase — safe to unit-test.
 *
 * CH-FASTPATH: When storefront === 'ch' we return `base` immediately, WITHOUT
 * reading from the override map. This is the mechanical guarantee for CH
 * byte-identity: even if a caller ever passed a non-empty map by mistake,
 * CH output cannot change.
 */
export function resolveSeoText(
  base: string,
  storefront: Storefront,
  lookup: SeoOverrideLookup,
  overrides?: SeoOverrideMap,
): string {
  if (storefront === 'ch') return base; // CH-FASTPATH — no map read.
  const override = overrides?.get(seoOverrideKey(lookup));
  const resolved = typeof override === 'string' && override.length > 0 ? override : base;
  // IT last-mile rewrite: strips CH-only wording (Svizzera/TWINT/BigFollow.ch)
  // from any string that reached this point without an admin override having
  // already replaced it. CH-fastpath above guarantees CH is untouched; AT is
  // never rewritten.
  if (storefront === 'it') return rewriteForIT(resolved);
  if (shouldScrubForAL(storefront)) return rewriteForAL(resolved);
  return resolved;
}

/** Shared empty map (avoid per-render allocations on the CH fastpath). */
export const EMPTY_SEO_OVERRIDE_MAP: SeoOverrideMap = new Map();